import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { usersApi } from '@/api/endpoints/users'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  teacher: { id: string; first_name?: string; last_name?: string; email: string }
  onSaved?: (updated: Props['teacher']) => void
}

export default function EditTeacherModal({ open, onClose, teacher, onSaved }: Props) {
  const addToast = useUI((s) => s.addToast)
  const [firstName, setFirstName] = useState(teacher.first_name || '')
  const [lastName, setLastName] = useState(teacher.last_name || '')
  const [email, setEmail] = useState(teacher.email)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setFirstName(teacher.first_name || '')
    setLastName(teacher.last_name || '')
    setEmail(teacher.email)
  }, [teacher])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const updated = await usersApi.update(teacher.id, { first_name: firstName, last_name: lastName })
      addToast({ message: 'Teacher updated', type: 'success' })
      onSaved?.({ ...teacher, first_name: updated.first_name, last_name: updated.last_name, email: teacher.email })
      onClose()
    } catch (error: any) {
      addToast({ message: error.response?.data?.detail || 'Failed to update teacher', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 hover-raise"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <h3 className="text-lg font-semibold mb-2">Edit Teacher</h3>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
              <div>
                <input type="email" className="input-field w-full bg-gray-100 text-gray-500 cursor-not-allowed" placeholder="Email" value={email} readOnly disabled />
                <p className="text-xs text-gray-400 mt-1">Email (login) can't be changed here.</p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}



